import { useData } from "../../runtime/provider";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { useMemo } from "react";
import { randomUUID } from "expo-crypto";
import { createActionSubmit } from "@utils/createActionSubmit";
import { RouterInputs, RouterOutputs, trpc } from "@utils/trpc";
import { captureException } from "@utils/captureException";
import { getQueryKey } from "@trpc/react-query";
import { gamificationSummaryQueryKey } from "./useGamificationSummary";
import { getAnonymousId } from "@utils/rudderstack";

type Input = RouterInputs["accesses"]["createCheckout"];
type Output = RouterOutputs["accesses"]["createCheckout"];

const useAccessCreateCheckout = (
  opts: Partial<UseMutationOptions<Output, Error, Input>> = {},
) => {
  const data = useData();
  const { queryClient } = data;
  const { onError, ...rest } = opts;

  const mutation = useMutation({
    mutationFn: async (input: Input) => {
      const anonymousId = await getAnonymousId();
      return data.api.accesses.createCheckout.mutate({
        ...input,
        anonymousId,
      });
    },
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: gamificationSummaryQueryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey(trpc.accesses.getCheckoutQuote),
        }),
      ]).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "checkout",
          operation: "invalidate-checkout-cache",
        });
      });
    },
    onError: (error, data, ctx, mutationCtx) => {
      captureException(error, {
        channel: "error",
        area: "checkout",
        operation: "create-checkout",
        tags: { accessId: data.accessId },
      });
      onError?.(error, data, ctx, mutationCtx);
    },
    ...rest,
  });

  const { mutate, mutateAsync } = mutation;
  const submit = useMemo(
    () => createActionSubmit({ mutate, mutateAsync }, randomUUID),
    [mutate, mutateAsync],
  );
  return { ...mutation, ...submit };
};

export default useAccessCreateCheckout;
